// Refonte du suivi — enregistrement automatique d'un brouillon (lot 3, revu
// après la revue des lots 3-4).
//
// Règles :
//   * une seule file par version : jamais deux enregistrements en même temps ;
//   * chaque envoi porte la révision connue (compare-and-set) et retient la
//     révision renvoyée par la base ; une réponse sans révision est traitée
//     comme un conflit (on ne devine jamais la révision) ;
//   * un envoi par groupe de champs : un champ refusé ne bloque pas les autres ;
//   * rien ne se perd en silence :
//       - coupure : les modifications restent en attente et repartent seules ;
//       - conflit (autre onglet) : elles sont gardées, l'écran demande de
//         recharger ; au rechargement, un texte n'est réappliqué que si
//         l'autre onglet ne l'a pas changé, sinon Anissa choisit ;
//       - refus (règle métier) : la modification est mise de côté avec son
//         message, jamais renvoyée en boucle ; Anissa peut la réessayer ou la
//         retirer ;
//   * l'onglet ne se ferme pas sans avertissement tant qu'il reste quelque
//     chose à enregistrer, et rien n'est relancé après la fermeture de l'écran.

import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { SuiviErreur, enregistrerBrouillon, traduireErreurSuivi } from './suiviVersions';
import { creerBrouillonLocal } from './suiviBrouillonLocal';

export const ETATS_AUTOSAVE = Object.freeze({
  ENREGISTRE: 'enregistre',
  EN_ATTENTE: 'en_attente',
  ENREGISTREMENT: 'enregistrement',
  HORS_LIGNE: 'hors_ligne',
  CONFLIT: 'conflit',
  ERREUR: 'erreur',
});

export const LIBELLES_AUTOSAVE = Object.freeze({
  enregistre: 'Enregistré',
  en_attente: 'Modifications en attente…',
  enregistrement: 'Enregistrement…',
  hors_ligne: 'Hors connexion : tes modifications sont gardées et partiront dès que possible',
  conflit: 'Modifiée dans un autre onglet : recharge pour continuer (tes modifications sont gardées)',
  erreur: 'Pas enregistré',
});

// Champs envoyés ensemble (l'éditeur de la semaine enregistre les deux d'un
// coup ; les phases vont par paire). Tout autre champ part seul.
const GROUPES = [['meal_recipes', 'editorial_overrides'], ['protocol_phases', 'active_phase_id']];
// Champs repris par l'éditeur de la semaine lui-même au rechargement
// (mergeWeekMenusDraft) : jamais renvoyés tels quels par-dessus l'autre onglet.
export const CHAMPS_FUSIONNES_PAR_L_EDITEUR = Object.freeze(['meal_recipes', 'editorial_overrides']);

function groupeDe(cle) {
  return GROUPES.find((groupe) => groupe.includes(cle)) || [cle];
}

function memeValeur(a, b) {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

function sansCles(objet, cles) {
  const copie = { ...objet };
  for (const cle of cles) delete copie[cle];
  return copie;
}

/** Reste-t-il quelque chose qui n'est pas en base (pur, pour l'écran) ? */
export function etatNonEnregistre(etat) {
  return Object.keys(etat?.enAttente || {}).length > 0
    || etat?.statut === ETATS_AUTOSAVE.ENREGISTREMENT
    || (etat?.refuses || []).length > 0
    || (etat?.choix || []).length > 0;
}

/**
 * Contrôleur sans React (testable seul). `enregistrer` reçoit
 * { consultationId, revision, changes } et renvoie la ligne à jour.
 */
export function creerAutosaveSuivi({
  ligne,
  enregistrer = (params) => enregistrerBrouillon(params),
  delai = 1500,
  delaiReprise = 5000,
  planifier = (fn, ms) => setTimeout(fn, ms),
  annuler = (id) => clearTimeout(id),
  // Copie locale des modifications pas encore en base (null = aucune).
  local = creerBrouillonLocal(),
} = {}) {
  let etat = {
    ligne,
    revision: ligne?.followup_edit_revision ?? 0,
    enAttente: {},
    // Valeur en base au moment où chaque champ en attente a commencé à changer.
    base: {},
    // Modifications refusées par une règle : { id, changes, message }.
    refuses: [],
    // Après un rechargement : { champ, mien, serveur } à trancher par Anissa.
    choix: [],
    statut: ETATS_AUTOSAVE.ENREGISTRE,
    erreur: null,
    enregistreA: null,
    // Message affiché quand des modifications locales ont été reprises.
    restaure: null,
  };
  const abonnes = new Set();
  let minuterie = null;
  let file = Promise.resolve();
  let detruit = false;
  let prochainRefus = 1;

  // Copie locale : textes et phases seulement. Les menus et recettes
  // (meal_recipes, editorial_overrides) ne sont JAMAIS copiés dans le
  // navigateur, même en attente d'envoi : l'éditeur de la semaine les
  // reprend lui-même depuis la base.
  const garderLocalement = () => {
    if (!local || !etat.ligne?.id) return;
    const enVol = etat.enVol || {};
    const enAttente = sansCles({ ...enVol, ...etat.enAttente }, CHAMPS_FUSIONNES_PAR_L_EDITEUR);
    for (const c of etat.choix) {
      if (!(c.champ in enAttente) && !CHAMPS_FUSIONNES_PAR_L_EDITEUR.includes(c.champ)) enAttente[c.champ] = c.mien;
    }
    const refuses = etat.refuses
      .map((r) => ({ ...r, changes: sansCles(r.changes || {}, CHAMPS_FUSIONNES_PAR_L_EDITEUR) }))
      .filter((r) => Object.keys(r.changes).length > 0);
    if (Object.keys(enAttente).length || refuses.length) {
      local.ecrire(etat.ligne.id, etat.revision, { enAttente, refuses });
    } else if (etat.statut !== ETATS_AUTOSAVE.ENREGISTREMENT) {
      local.effacer(etat.ligne.id);
    }
  };
  const publier = (patch) => {
    etat = { ...etat, ...patch };
    garderLocalement();
    for (const abonne of abonnes) abonne();
  };
  const aDesChangements = () => Object.keys(etat.enAttente).length > 0;
  const arreterMinuterie = () => {
    if (minuterie !== null) annuler(minuterie);
    minuterie = null;
  };
  const programmer = (ms) => {
    arreterMinuterie();
    if (detruit) return;
    minuterie = planifier(() => {
      minuterie = null;
      lancer().catch(() => {});
    }, ms);
  };
  const statutAuRepos = () => {
    if (etat.refuses.length > 0) return ETATS_AUTOSAVE.ERREUR;
    return aDesChangements() ? ETATS_AUTOSAVE.EN_ATTENTE : ETATS_AUTOSAVE.ENREGISTRE;
  };
  const oublierBase = (cles) => {
    const base = { ...etat.base };
    for (const cle of cles) {
      if (!(cle in etat.enAttente) && !etat.refuses.some((r) => cle in r.changes)) delete base[cle];
    }
    publier({ base });
  };

  async function envoyerUnGroupe() {
    const premiere = Object.keys(etat.enAttente)[0];
    const cles = groupeDe(premiere).filter((cle) => cle in etat.enAttente);
    const lot = Object.fromEntries(cles.map((cle) => [cle, etat.enAttente[cle]]));
    publier({ enAttente: sansCles(etat.enAttente, cles), enVol: lot, statut: ETATS_AUTOSAVE.ENREGISTREMENT, erreur: null });
    let ligneAJour;
    try {
      ligneAJour = await enregistrer({ consultationId: etat.ligne.id, revision: etat.revision, changes: lot });
      if (!Number.isInteger(ligneAJour?.followup_edit_revision)) {
        throw new SuiviErreur('Enregistrement non confirmé par le serveur : recharge pour continuer.', { nature: 'conflit' });
      }
    } catch (cause) {
      const erreur = traduireErreurSuivi(cause);
      if (erreur.nature === 'reseau' || erreur.nature === 'conflit') {
        // Les modifications faites pendant l'envoi priment sur le lot échoué.
        publier({
          enAttente: { ...lot, ...etat.enAttente },
          enVol: null,
          erreur,
          statut: erreur.nature === 'reseau' ? ETATS_AUTOSAVE.HORS_LIGNE : ETATS_AUTOSAVE.CONFLIT,
        });
        if (erreur.nature === 'reseau') programmer(delaiReprise);
        throw erreur;
      }
      // Refus : mis de côté, jamais renvoyé en boucle ni mêlé aux suivants.
      const encoreLa = Object.fromEntries(Object.entries(lot).filter(([cle]) => !(cle in etat.enAttente)));
      const refuses = Object.keys(encoreLa).length
        ? [...etat.refuses, { id: prochainRefus++, changes: encoreLa, message: erreur.message }]
        : etat.refuses;
      publier({ refuses, enVol: null, erreur });
      return erreur;
    }
    const base = { ...etat.base };
    for (const cle of cles) {
      if (cle in etat.enAttente) base[cle] = ligneAJour[cle];
      else delete base[cle];
    }
    publier({
      ligne: ligneAJour,
      revision: ligneAJour.followup_edit_revision,
      base,
      enVol: null,
      enregistreA: new Date().toISOString(),
    });
    return null;
  }

  async function envoyer() {
    if (detruit || !aDesChangements()) return etat.ligne;
    if (etat.statut === ETATS_AUTOSAVE.CONFLIT) throw etat.erreur;
    arreterMinuterie();
    let premierRefus = null;
    while (!detruit && aDesChangements()) {
      const refus = await envoyerUnGroupe();
      premierRefus = premierRefus || refus;
    }
    const derniere = etat.refuses[etat.refuses.length - 1];
    publier({
      statut: statutAuRepos(),
      erreur: etat.refuses.length ? (premierRefus || etat.erreur || new SuiviErreur(derniere.message)) : null,
    });
    if (premierRefus) throw premierRefus;
    return etat.ligne;
  }

  /** Modification au fil de la saisie : envoyée après `delai`. */
  function modifier(changes) {
    if (!changes || Object.keys(changes).length === 0) return;
    const base = { ...etat.base };
    for (const cle of Object.keys(changes)) {
      if (!(cle in base)) base[cle] = etat.ligne?.[cle] ?? null;
    }
    // Une nouvelle valeur remplace une valeur refusée du même champ.
    const refuses = etat.refuses
      .map((r) => ({ ...r, changes: sansCles(r.changes, Object.keys(changes)) }))
      .filter((r) => Object.keys(r.changes).length > 0);
    const choix = etat.choix.filter((c) => !(c.champ in changes));
    const enAttente = { ...etat.enAttente, ...changes };
    const bloque = etat.statut === ETATS_AUTOSAVE.CONFLIT || etat.statut === ETATS_AUTOSAVE.HORS_LIGNE;
    publier({
      base, refuses, choix, enAttente,
      statut: bloque ? etat.statut : ETATS_AUTOSAVE.EN_ATTENTE,
      erreur: bloque ? etat.erreur : (refuses.length ? etat.erreur : null),
    });
    if (!bloque) programmer(delai);
  }

  // Copie locale laissée par une visite précédente : reprise, ou à trancher
  // si la version a changé depuis (jamais renvoyée à l'aveugle).
  function restaurer() {
    const copies = local && ligne?.id ? local.lire(ligne.id) : [];
    if (!copies.length) return;
    const copie = [...copies].sort((a, b) => String(b.le || '').localeCompare(String(a.le || '')))[0];
    if (copie.revision === etat.revision) {
      publier({
        enAttente: { ...(copie.enAttente || {}) },
        refuses: (copie.refuses || []).map((r) => ({ ...r, id: prochainRefus++ })),
        statut: ETATS_AUTOSAVE.EN_ATTENTE,
        restaure: 'Des modifications pas encore enregistrées lors de ta dernière visite ont été reprises : elles partent maintenant.',
      });
      return;
    }
    const choix = [];
    let menusPerdus = false;
    for (const [champ, valeur] of Object.entries(copie.enAttente || {})) {
      if (CHAMPS_FUSIONNES_PAR_L_EDITEUR.includes(champ)) { menusPerdus = true; continue; }
      if (!memeValeur(etat.ligne?.[champ], valeur)) choix.push({ champ, mien: valeur, serveur: etat.ligne?.[champ] ?? null });
    }
    publier({
      choix,
      restaure: choix.length || menusPerdus
        ? 'Des modifications de ta dernière visite n’avaient pas été enregistrées et la version a changé depuis'
          + (choix.length ? ' : choisis ci-dessous quoi garder.' : '.')
          + (menusPerdus ? ' Celles des menus n’ont pas pu être reprises.' : '')
        : null,
    });
    if (!choix.length) local.effacer(ligne.id);
  }
  restaurer();

  function lancer() {
    const suite = file.then(envoyer, envoyer);
    file = suite.catch(() => {});
    return suite;
  }

  return {
    getEtat: () => etat,
    abonner(fn) {
      abonnes.add(fn);
      return () => abonnes.delete(fn);
    },
    modifier,
    /** Enregistre tout de suite (bouton « Enregistrer » d'un éditeur). */
    enregistrerMaintenant(changes = {}) {
      if (changes && Object.keys(changes).length) modifier(changes);
      arreterMinuterie();
      return lancer();
    },
    /** Relance après une erreur ou une coupure (y compris les refus). */
    reessayer() {
      if (etat.statut === ETATS_AUTOSAVE.CONFLIT) return Promise.reject(etat.erreur);
      const remis = Object.assign({}, ...etat.refuses.map((r) => r.changes));
      publier({ enAttente: { ...remis, ...etat.enAttente }, refuses: [], erreur: null, statut: ETATS_AUTOSAVE.EN_ATTENTE });
      arreterMinuterie();
      return lancer();
    },
    /** Réessaie une seule modification refusée. */
    reessayerRefus(id) {
      const refus = etat.refuses.find((r) => r.id === id);
      if (!refus || etat.statut === ETATS_AUTOSAVE.CONFLIT) return Promise.resolve(etat.ligne);
      publier({
        enAttente: { ...refus.changes, ...etat.enAttente },
        refuses: etat.refuses.filter((r) => r.id !== id),
      });
      arreterMinuterie();
      return lancer();
    },
    /** « Retirer cette modification » : la valeur en base reste. Rend les champs retirés. */
    retirerRefus(id) {
      const refus = etat.refuses.find((r) => r.id === id);
      if (!refus) return [];
      const refuses = etat.refuses.filter((r) => r.id !== id);
      publier({ refuses });
      oublierBase(Object.keys(refus.changes));
      publier({ statut: etat.statut === ETATS_AUTOSAVE.ERREUR ? statutAuRepos() : etat.statut, erreur: refuses.length ? etat.erreur : null });
      return Object.keys(refus.changes);
    },
    /**
     * Après un conflit : repart de la version relue.
     *   * menus et mise en page (meal_recipes, editorial_overrides) : jamais
     *     renvoyés, l'éditeur de la semaine fusionne lui-même son brouillon ;
     *   * un autre champ est réappliqué s'il n'a pas changé dans l'autre
     *     onglet ; s'il a changé, Anissa choisit (etat.choix).
     * `reprendre: false` écarte tout, sur demande explicite d'Anissa.
     */
    recharger(nouvelleLigne, { reprendre = true } = {}) {
      arreterMinuterie();
      const enAttente = {};
      const choix = [];
      const base = {};
      if (reprendre) {
        for (const [cle, valeur] of Object.entries(etat.enAttente)) {
          if (CHAMPS_FUSIONNES_PAR_L_EDITEUR.includes(cle)) continue;
          const serveur = nouvelleLigne?.[cle] ?? null;
          if (memeValeur(serveur, valeur)) continue;
          if (memeValeur(serveur, etat.base[cle])) {
            enAttente[cle] = valeur;
            base[cle] = serveur;
          } else {
            choix.push({ champ: cle, mien: valeur, serveur });
          }
        }
      }
      publier({
        ligne: nouvelleLigne,
        revision: nouvelleLigne?.followup_edit_revision ?? 0,
        enAttente,
        base,
        choix,
        refuses: reprendre ? etat.refuses : [],
        erreur: null,
        statut: Object.keys(enAttente).length ? ETATS_AUTOSAVE.EN_ATTENTE : ETATS_AUTOSAVE.ENREGISTRE,
      });
      if (Object.keys(enAttente).length) programmer(delai);
    },
    /** Choix après rechargement : garder sa version du champ. */
    garderMaVersion(champ) {
      const c = etat.choix.find((item) => item.champ === champ);
      if (!c) return;
      publier({ choix: etat.choix.filter((item) => item.champ !== champ) });
      modifier({ [champ]: c.mien });
    },
    /** Choix après rechargement : garder la version de l'autre onglet. */
    garderAutreVersion(champ) {
      publier({ choix: etat.choix.filter((item) => item.champ !== champ) });
    },
    /** Reste-t-il quelque chose qui n'est pas en base ? */
    aDuNonEnregistre() {
      return etatNonEnregistre(etat);
    },
    detruire() {
      detruit = true;
      arreterMinuterie();
    },
    reactiver() {
      detruit = false;
    },
    /** Reprogramme l'envoi s'il reste des modifications (montage, abandon refusé). */
    relancer() {
      if (detruit || !aDesChangements()) return;
      if (etat.statut === ETATS_AUTOSAVE.CONFLIT) return;
      programmer(etat.statut === ETATS_AUTOSAVE.HORS_LIGNE ? delaiReprise : delai);
    },
    /** Oublie la copie locale (brouillon abandonné). */
    oublierLocal() {
      if (local && etat.ligne?.id) local.effacer(etat.ligne.id);
    },
    /**
     * Arrête la file sans rien envoyer de plus et attend l'envoi en cours :
     * la ligne rendue porte la dernière révision (abandon du brouillon).
     */
    async arreter() {
      detruit = true;
      arreterMinuterie();
      await file;
      return etat.ligne;
    },
  };
}

/**
 * Hook React autour du contrôleur. Un contrôleur par montage : le composant
 * qui l'utilise est monté avec key={version.id}, donc une version = une file.
 */
export default function useSuiviAutosave(ligne, options = {}) {
  const [controleur] = useState(() => creerAutosaveSuivi({ ligne, ...options }));
  const montages = useRef(0);
  const etat = useSyncExternalStore(
    useCallback((fn) => controleur.abonner(fn), [controleur]),
    controleur.getEtat,
    controleur.getEtat,
  );

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const avertir = (event) => {
      if (!controleur.aDuNonEnregistre()) return undefined;
      event.preventDefault();
      event.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', avertir);
    return () => window.removeEventListener('beforeunload', avertir);
  }, [controleur]);

  // En quittant l'écran, ce qui reste en attente part tout de suite ; une fois
  // cet envoi terminé, le contrôleur s'arrête (plus aucune relance). Le
  // compteur de montages évite d'arrêter un contrôleur remonté (StrictMode).
  useEffect(() => {
    montages.current += 1;
    const moi = montages.current;
    controleur.reactiver();
    controleur.relancer();
    return () => {
      const fin = () => { if (montages.current === moi) controleur.detruire(); };
      if (controleur.aDuNonEnregistre()) controleur.enregistrerMaintenant().then(fin, fin);
      else fin();
    };
  }, [controleur]);

  return {
    etat,
    libelle: LIBELLES_AUTOSAVE[etat.statut],
    nonEnregistre: etatNonEnregistre(etat),
    modifier: controleur.modifier,
    enregistrerMaintenant: (changes) => controleur.enregistrerMaintenant(changes),
    reessayer: controleur.reessayer,
    reessayerRefus: controleur.reessayerRefus,
    retirerRefus: controleur.retirerRefus,
    recharger: controleur.recharger,
    garderMaVersion: (champ) => controleur.garderMaVersion(champ),
    garderAutreVersion: (champ) => controleur.garderAutreVersion(champ),
    aDuNonEnregistre: controleur.aDuNonEnregistre,
    detruire: controleur.detruire,
    reactiver: controleur.reactiver,
    relancer: controleur.relancer,
    oublierLocal: controleur.oublierLocal,
    arreter: controleur.arreter,
    lireEtat: controleur.getEtat,
  };
}
